import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Chain } from '../../datas/chains';
import { RootState } from '../store';
import { HistoryPayload } from '../../services/bridge-backend.service';

const ITEMS_PER_PAGE = 10;

const KSTADIUM_BRIDGE_BACKEND_URL =
  process.env.REACT_APP_KSTADIUM_BRIDGE_BACKEND_URL!;

export type HistoryChainState =
  | 'Confirmation needed'
  | 'Confirmed'
  | 'Pending'
  | 'Failed';

export interface HTLC {
  id: number;
  sender: string;
  vaultAddress: string;
  amount: string;
  timelock: number;
  preimage: string;
  tokenAddress: string;
  fromContractId: string;
  toContractId: string;
}

type HistoryState = {
  histories: HistoryPayload[];
  pageNumber: number;
  lastPageNumber: number;
  searchText: string;
  filterChain: null | any;
  fromRefundHistoryTx: Chain | null;
  toRefundHistoryTx: Chain | null;
};

const initialState: HistoryState = {
  histories: [],
  pageNumber: 1,
  lastPageNumber: 1,
  searchText: ``,
  filterChain: null,
  fromRefundHistoryTx: null,
  toRefundHistoryTx: null,
};

type HistoryListResult = {
  data: HistoryPayload[];
  totalCount: number;
};

export const getHistories = createAsyncThunk<
  HistoryListResult,
  {
    chainSymbol: string;
    sender: string | undefined;
    skip: number;
    take: number;
  }
>(`history/getHistories`, async ({ chainSymbol, sender, skip, take }) => {
  // TODO : API 연동
  const searchParams = new URLSearchParams();
  searchParams.append(`skip`, `${skip}`);
  searchParams.append(`take`, `${take}`);
  const res = await fetch(
    `${KSTADIUM_BRIDGE_BACKEND_URL}/api/histories/${chainSymbol}/senders/${sender}?${searchParams.toString()}`,
  );
  const result: HistoryListResult = await res.json();
  return result;
});

const historySlice = createSlice({
  name: `history`,
  initialState,
  reducers: {
    reset: (state) => ({
      ...initialState,
    }),
    goPage: (state, action) => {
      state.pageNumber = action.payload;
    },
    goPrevious: (state) => {
      state.pageNumber = Math.max(1, state.pageNumber - 5);
    },
    goNext: (state) => {
      state.pageNumber = Math.min(state.lastPageNumber!, state.pageNumber + 5);
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setFilterChain: (state, action) => {
      state.filterChain = action.payload;
    },

    setFromRefundHistoryTx: (state, action) => {
      state.fromRefundHistoryTx = action.payload;
    },

    setToRefundHistoryTx: (state, action) => {
      state.toRefundHistoryTx = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHistories.fulfilled, (state, action) => {
      state.histories = action.payload.data;
      state.lastPageNumber = Math.ceil(
        action.payload.totalCount / ITEMS_PER_PAGE,
      );
    });
  },
});

export const {
  reset,
  goNext,
  goPage,
  goPrevious,
  setFilterChain,
  setSearchText,
  setFromRefundHistoryTx,
  setToRefundHistoryTx,
} = historySlice.actions;

export default historySlice.reducer;

export const selectHistorySlice = (state: RootState): HistoryState =>
  state.history;
